'use client';

import { AppSidebar } from '@/components/layout/app-sidebar';
import { FilterBar } from '@/components/layout/filter-bar';
import { NotificationBell } from '@/components/layout/notification-bell';
import { TabContent } from '@/components/layout/tab-content';
import { TabHeader, type Tab } from '@/components/layout/tab-header';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useRBAC } from '@/contexts/rbac-context';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Home } from 'lucide-react';
import * as React from 'react';
import RealGrid from 'realgrid';
import 'realgrid/dist/realgrid-style.css';

// Prefer runtime-provided key (window.__ENV__) and fallback to build-time env

const runtimeEnv: any = typeof window !== 'undefined' ? (window as any).__ENV__ : undefined;
const runtimeLicenseKey = runtimeEnv?.REALGRID_LICENSE_KEY as string | undefined;
const buildTimeLicenseKey = import.meta.env.VITE_REALGRID_LICENSE_KEY as string | undefined;
const effectiveLicenseKey = runtimeLicenseKey || buildTimeLicenseKey || '';

if (effectiveLicenseKey) {
  RealGrid.setLicenseKey(effectiveLicenseKey);
}

export function MainPage() {
  const { canAccessMenu } = useRBAC();
  const [tabs, setTabs] = React.useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = React.useState<string | null>(null);

  useDocumentTitle('메인');

  // 최초 진입 시 Dashboard 탭 자동 오픈. RBAC 은 /me/menus 비동기 로드라 권한이 확정된 뒤에 연다.
  // dep 는 함수가 아니라 boolean 이라 false→true 한 번만 발화한다 (닫은 탭이 다시 열리지 않는다)
  const canSeeDashboard = canAccessMenu('dashboard');
  React.useEffect(() => {
    if (!canSeeDashboard) return;
    setTabs([{ id: 'dashboard', title: 'Dashboard', icon: Home }]);
    setActiveTabId('dashboard');
  }, [canSeeDashboard]);

  // TabContent의 memo 화면(TabPane)에 넘어가므로 참조가 바뀌면 안 된다. tabs는 함수형 업데이트로 읽는다.
  const handleOpenTab = React.useCallback(
    (item: Tab) => {
      setTabs(prev =>
        prev.some(tab => tab.id === item.id) ? prev : [...prev, { ...item }]
      );
      setActiveTabId(item.id);
    },
    []
  );

  const handleTabClick = (tabId: string) => {
    setActiveTabId(tabId);
  };

  const handleTabClose = (tabId: string) => {
    setTabs(prev => {
      const newTabs = prev.filter(tab => tab.id !== tabId);

      // If we're closing the active tab, switch to another tab
      if (activeTabId === tabId) {
        if (newTabs.length > 0) {
          // Switch to the tab that was before the closed one, or the first one
          const closedIndex = prev.findIndex(tab => tab.id === tabId);
          const newActiveIndex = closedIndex > 0 ? closedIndex - 1 : 0;
          setActiveTabId(newTabs[newActiveIndex]?.id || null);
        } else {
          setActiveTabId(null);
        }
      }

      return newTabs;
    });
  };

  const activeTab = tabs.find(tab => tab.id === activeTabId) || null;

  return (
    <SidebarProvider>
      <AppSidebar onOpenTab={handleOpenTab} />
      <SidebarInset className='min-w-0'>
        <div className='flex flex-col h-screen'>
          <TabHeader
            tabs={tabs}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            onTabClose={handleTabClose}
            trailing={<NotificationBell onOpenTab={handleOpenTab} />}
          />

          {/* 필터바 - 탭 영역 바로 아래에 위치, 현재 탭에 따라 표시되는 필터가 달라짐 */}
          <FilterBar currentPageId={activeTabId || 'default'} />

          <TabContent tabs={tabs} activeTab={activeTab} onOpenTab={handleOpenTab} />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
